'use client';

import { c as _c } from "react/compiler-runtime";
import type { RefObject } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedOutcomePercent } from '@/components/markets/animated-outcome-percent';
import { outcomeLabelTooltipArrowClassName, outcomeLabelTooltipClassName, outcomeLabelTooltipContentStyle, useOutcomeLabelTruncation } from '@/components/markets/outcome-label-tooltip';
import { AppTooltip } from '@/components/ui/app-tooltip';
import { DrainedChip } from '@/components/ui/drained-chip';
import { Icon } from '@/components/ui/icon';
import { Award01IconSolid, Tick02Icon } from '@/components/ui/icon/glyphs';
import { type OutcomeColorClasses, useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { cn } from '@/lib/ui/utils';
export type OutcomeCardVariant = 'interactive' | 'static' | 'winner';
export interface OutcomeCardProps {
  /** Stable parent entity address used to scope the outcome image identity. */
  entityPubkey: string;
  index: number;
  label: string;
  colorOutcomes?: ReadonlyArray<{
    title: string;
    index: number;
  }> | undefined;
  /** Already-computed percent (0-100). The card never derives it from trading state. */
  percent: number;
  /** Consensus goal percentage shown after the current outcome percentage. */
  goalPercent?: number | undefined;
  /** Hide the percentage entirely — used by the winner when consensus is unparseable. */
  showPercent?: boolean;
  /** Render the percentage with two decimals (live trading view). Resolved views pass `false`. */
  decimals?: boolean;
  imageUrl?: string | null;
  imageNsfw?: boolean;
  /** Accessible name for the image. Defaults to empty (decorative) — the
   *  winner passes a descriptive alt for a11y (FR-021). */
  imageAlt?: string;
  /**
   * Enable the click-to-zoom lightbox preview on the outcome image. Default
   * `false`. For the interactive variant the image becomes its own preview
   * control (its own button) while the rest of the card stays the select
   * target — achieved with the overlay-select layout below, so the two
   * controls never nest as buttons.
   */
  imagePreview?: boolean;
  ticker?: string | null;
  variant: OutcomeCardVariant;
  /** Interactive only — current selection state (drives `aria-pressed`). */
  isSelected?: boolean;
  /** Interactive only — selecting the outcome. When omitted the card is inert. */
  onSelect?: ((index: number) => void) | undefined;
  /** Interactive only — accessible name for the selection control. */
  ariaLabel?: string;
  /** Live view only — category emoji shown when the outcome has no image. */
  fallbackEmoji?: string;
  /** Static (loser) only — render the `Drained` chip for the active wallet's dead position. */
  drained?: boolean;
  /** Already-translated `Drained` label. */
  drainedLabel?: string;
  /** Winner only — already-translated "Winner" label. */
  winnerLabel?: string;
  /** Winner only — already-translated caption under the percent (e.g. "Final consensus"). */
  caption?: string;
  /** Renderer-owned display leaf; bypasses AnimatedOutcomePercent. */
  percentContent?: React.ReactNode;
  /** Renderer-owned goal leaf. */
  goalContent?: React.ReactNode;
  selectionControlRef?: RefObject<HTMLButtonElement | null>;
}

/**
 * Shared outcome card — the single visual for one market outcome, used by the
 * live Outcomes panel (interactive) and the Resolved panel (static losers +
 * elevated winner). The percentage is always an input prop so the same card
 * renders live implied odds or a settled final share without any internal data
 * dependency. See spec 109.
 */
export function OutcomeCard(props) {
  const $ = _c(80);
  const {
    entityPubkey,
    index,
    label,
    imageUrl: t0,
    imageNsfw: t1,
    imageAlt,
    imagePreview: t2,
    ticker: t3,
    variant,
    isSelected: t4,
    onSelect,
    ariaLabel,
    fallbackEmoji,
    selectionControlRef
  } = props;
  const imageUrl = t0 === undefined ? null : t0;
  const imageNsfw = t1 === undefined ? false : t1;
  const imagePreview = t2 === undefined ? false : t2;
  const ticker = t3 === undefined ? null : t3;
  const isSelected = t4 === undefined ? false : t4;
  let t5;
  if ($[0] !== index || $[1] !== label) {
    t5 = {
      title: label,
      index
    };
    $[0] = index;
    $[1] = label;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== entityPubkey || $[4] !== props.colorOutcomes) {
    t6 = {
      marketPubkey: entityPubkey,
      marketOutcomes: props.colorOutcomes
    };
    $[3] = entityPubkey;
    $[4] = props.colorOutcomes;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const colors = useOutcomeColor(t5, t6);
  const {
    setLabelSpanEl,
    isTruncated
  } = useOutcomeLabelTruncation(label);
  const hasImage = imageUrl !== null && imageUrl !== "";
  const showEmoji = !hasImage && fallbackEmoji !== undefined;
  let t7;
  if ($[6] !== colors.cta || $[7] !== colors.hue || $[8] !== entityPubkey || $[9] !== fallbackEmoji || $[10] !== imageAlt || $[11] !== imageNsfw || $[12] !== imagePreview || $[13] !== imageUrl || $[14] !== index || $[15] !== showEmoji) {
    t7 = (sizeClass, rounded) => showEmoji ? <span className={cn("relative flex shrink-0 items-center justify-center overflow-hidden", sizeClass, rounded, colors.cta)}><span aria-hidden={true} className="flex size-full items-center justify-center text-xl leading-none">{fallbackEmoji}</span></span> : <span className={cn("relative flex shrink-0 items-center justify-center overflow-hidden", sizeClass, rounded, colors.cta, imagePreview && "pointer-events-auto")}><OptimizedImage variant="thumbnail" entity={{
        kind: "outcome",
        marketPubkey: entityPubkey,
        outcomeIndex: index,
        hue: colors.hue,
        imageUrl: imageUrl ?? null,
        nsfw: imageNsfw
      }} alt={imageAlt ?? ""} size={sizeClass} rounded={rounded} preview={imagePreview} loadReveal="none" className="size-full object-cover" /></span>;
    $[6] = colors.cta;
    $[7] = colors.hue;
    $[8] = entityPubkey;
    $[9] = fallbackEmoji;
    $[10] = imageAlt;
    $[11] = imageNsfw;
    $[12] = imagePreview;
    $[13] = imageUrl;
    $[14] = index;
    $[15] = showEmoji;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const mediaBlock = t7;
  let t8;
  if ($[17] !== colors.text || $[18] !== ticker) {
    t8 = ticker !== null ? <span className={cn("shrink-0 rounded-item bg-background-tertiary px-1.5 py-0.5 font-mono text-caption font-medium tracking-wide", colors.text)}>{ticker}</span> : null;
    $[17] = colors.text;
    $[18] = ticker;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const tickerChip = t8;
  if (variant === "winner") {
    let t9;
    if ($[20] !== mediaBlock) {
      t9 = mediaBlock("size-12", "rounded-panel-inset");
      $[20] = mediaBlock;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    let t10;
    if ($[22] !== colors || $[23] !== props || $[24] !== setLabelSpanEl || $[25] !== t9 || $[26] !== tickerChip) {
      t10 = <WinnerOutcomeContent {...props} colors={colors} media={t9} ticker={tickerChip} labelRef={setLabelSpanEl} />;
      $[22] = colors;
      $[23] = props;
      $[24] = setLabelSpanEl;
      $[25] = t9;
      $[26] = tickerChip;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    const winnerInner = t10;
    const t11 = !isTruncated;
    let t12;
    if ($[28] !== colors) {
      t12 = outcomeLabelTooltipClassName(colors);
      $[28] = colors;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    let t13;
    if ($[30] !== colors) {
      t13 = outcomeLabelTooltipContentStyle(colors);
      $[30] = colors;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    let t14;
    if ($[32] !== colors) {
      t14 = outcomeLabelTooltipArrowClassName(colors);
      $[32] = colors;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    let t15;
    if ($[34] !== label || $[35] !== t11 || $[36] !== t12 || $[37] !== t13 || $[38] !== t14 || $[39] !== winnerInner) {
      t15 = <AppTooltip content={label} disabled={t11} side="bottom" align="center" delayDuration={100} contentClassName={t12} contentStyle={t13} arrowClassName={t14}>{winnerInner}</AppTooltip>;
      $[34] = label;
      $[35] = t11;
      $[36] = t12;
      $[37] = t13;
      $[38] = t14;
      $[39] = winnerInner;
      $[40] = t15;
    } else {
      t15 = $[40];
    }
    return t15;
  }
  let t9;
  if ($[41] !== ariaLabel || $[42] !== colors || $[43] !== index || $[44] !== isSelected || $[45] !== mediaBlock || $[46] !== onSelect || $[47] !== props || $[48] !== selectionControlRef || $[49] !== setLabelSpanEl || $[50] !== tickerChip || $[51] !== variant) {
    const surfaceClass = cn("relative box-border flex min-w-0 w-full flex-row items-center gap-2 rounded-overlay border-solid [border-width:var(--border-width-hairline)] border-transparent p-2 text-left", variant === "interactive" ? colors.fill : colors.outline, variant === "interactive" && isSelected && `border-outcome-${colors.hue}-text`);
    let t10;
    if ($[53] !== mediaBlock) {
      t10 = mediaBlock("size-10", "rounded-item");
      $[53] = mediaBlock;
      $[54] = t10;
    } else {
      t10 = $[54];
    }
    let t11;
    if ($[55] !== colors.text || $[56] !== isSelected || $[57] !== variant) {
      t11 = variant === "interactive" && isSelected && <span aria-hidden={true} data-testid="outcome-card-selected-icon" className="pointer-events-none absolute -right-1 -bottom-1 flex size-4 items-center justify-center"><span data-testid="outcome-card-selected-icon-background" className="absolute inset-0 rounded-full bg-background-primary/50 backdrop-blur-sm" /><Icon icon={Tick02Icon} className={cn("relative size-3", colors.text)} /></span>;
      $[55] = colors.text;
      $[56] = isSelected;
      $[57] = variant;
      $[58] = t11;
    } else {
      t11 = $[58];
    }
    let t12;
    if ($[59] !== t10 || $[60] !== t11) {
      t12 = <span className="relative shrink-0">{t10}{t11}</span>;
      $[59] = t10;
      $[60] = t11;
      $[61] = t12;
    } else {
      t12 = $[61];
    }
    const compactMediaBlock = t12;
    let t13;
    if ($[62] !== colors || $[63] !== props || $[64] !== setLabelSpanEl || $[65] !== tickerChip) {
      t13 = <CompactOutcomeText {...props} colors={colors} ticker={tickerChip} labelRef={setLabelSpanEl} />;
      $[62] = colors;
      $[63] = props;
      $[64] = setLabelSpanEl;
      $[65] = tickerChip;
      $[66] = t13;
    } else {
      t13 = $[66];
    }
    const textStack = t13;
    t9 = variant === "interactive" && onSelect !== undefined ? <div className={cn(surfaceClass, "cursor-pointer")} data-testid="outcome-card" data-variant="interactive"><button ref={selectionControlRef} type="button" className={cn("absolute inset-0 cursor-pointer rounded-overlay border-solid [border-width:var(--border-width-hairline)] border-transparent outline-none", `focus-visible:border-outcome-${colors.hue}-text`)} onClick={() => {
        onSelect(index);
      }} aria-label={ariaLabel} aria-pressed={isSelected} /><span className="pointer-events-none relative z-10 flex min-w-0 flex-1 flex-row items-center gap-2">{compactMediaBlock}{textStack}</span></div> : <div className={surfaceClass} data-testid="outcome-card" data-variant="static">{compactMediaBlock}{textStack}</div>;
    $[41] = ariaLabel;
    $[42] = colors;
    $[43] = index;
    $[44] = isSelected;
    $[45] = mediaBlock;
    $[46] = onSelect;
    $[47] = props;
    $[48] = selectionControlRef;
    $[49] = setLabelSpanEl;
    $[50] = tickerChip;
    $[51] = variant;
    $[52] = t9;
  } else {
    t9 = $[52];
  }
  const chip = t9;
  const t10 = !isTruncated;
  let t11;
  if ($[67] !== colors) {
    t11 = outcomeLabelTooltipClassName(colors);
    $[67] = colors;
    $[68] = t11;
  } else {
    t11 = $[68];
  }
  let t12;
  if ($[69] !== colors) {
    t12 = outcomeLabelTooltipContentStyle(colors);
    $[69] = colors;
    $[70] = t12;
  } else {
    t12 = $[70];
  }
  let t13;
  if ($[71] !== colors) {
    t13 = outcomeLabelTooltipArrowClassName(colors);
    $[71] = colors;
    $[72] = t13;
  } else {
    t13 = $[72];
  }
  let t14;
  if ($[73] !== chip || $[74] !== label || $[75] !== t10 || $[76] !== t11 || $[77] !== t12 || $[78] !== t13) {
    t14 = <AppTooltip content={label} disabled={t10} side="bottom" align="center" delayDuration={100} contentClassName={t11} contentStyle={t12} arrowClassName={t13}>{chip}</AppTooltip>;
    $[73] = chip;
    $[74] = label;
    $[75] = t10;
    $[76] = t11;
    $[77] = t12;
    $[78] = t13;
    $[79] = t14;
  } else {
    t14 = $[79];
  }
  return t14;
}
function WinnerOutcomeContent(t0) {
  const $ = _c(40);
  const {
    label,
    percent,
    showPercent: t1,
    decimals: t2,
    winnerLabel,
    caption,
    colors,
    media,
    ticker,
    labelRef
  } = t0;
  const showPercent = t1 === undefined ? true : t1;
  const decimals = t2 === undefined ? true : t2;
  let t3;
  if ($[0] !== colors.outline) {
    t3 = cn("flex w-full flex-col gap-3 rounded-panel-row border border-transparent p-3", colors.outline);
    $[0] = colors.outline;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== colors.text) {
    t4 = cn("size-4", colors.text);
    $[2] = colors.text;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4) {
    t5 = <Icon icon={Award01IconSolid} aria-hidden={true} className={t4} />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== colors.text || $[7] !== winnerLabel) {
    t6 = winnerLabel !== undefined && <span className={cn("text-caption font-medium uppercase tracking-wide", colors.text)}>{winnerLabel}</span>;
    $[6] = colors.text;
    $[7] = winnerLabel;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <div className="flex items-center gap-1.5">{t5}{t6}</div>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== colors.text) {
    t8 = cn("truncate text-body-lg font-medium", colors.text);
    $[12] = colors.text;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== label || $[15] !== labelRef || $[16] !== t8) {
    t9 = <span ref={labelRef} className={t8}>{label}</span>;
    $[14] = label;
    $[15] = labelRef;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9 || $[19] !== ticker) {
    t10 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t9}{ticker}</div>;
    $[18] = t9;
    $[19] = ticker;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== colors.text || $[22] !== decimals || $[23] !== percent || $[24] !== showPercent) {
    t11 = showPercent && <AnimatedOutcomePercent value={percent} decimals={decimals} animateOnMount={true} className={cn("text-heading-md font-bold tabular-nums", colors.text)} />;
    $[21] = colors.text;
    $[22] = decimals;
    $[23] = percent;
    $[24] = showPercent;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== caption || $[27] !== colors.text) {
    t12 = caption !== undefined && <span className={cn("text-micro uppercase tracking-wide", colors.text)}>{caption}</span>;
    $[26] = caption;
    $[27] = colors.text;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t11 || $[30] !== t12) {
    t13 = <div className="flex shrink-0 flex-col items-end">{t11}{t12}</div>;
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== media || $[33] !== t10 || $[34] !== t13) {
    t14 = <div className="flex items-center gap-3">{media}{t10}{t13}</div>;
    $[32] = media;
    $[33] = t10;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t14 || $[37] !== t3 || $[38] !== t7) {
    t15 = <div data-testid="outcome-card" data-variant="winner" className={t3}>{t7}{t14}</div>;
    $[36] = t14;
    $[37] = t3;
    $[38] = t7;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
}
function CompactOutcomeText(t0) {
  const $ = _c(26);
  const {
    label,
    percent,
    showPercent: t1,
    decimals: t2,
    drained: t3,
    drainedLabel,
    percentContent,
    goalContent,
    goalPercent,
    colors,
    ticker,
    labelRef
  } = t0;
  const showPercent = t1 === undefined ? true : t1;
  const decimals = t2 === undefined ? true : t2;
  const drained = t3 === undefined ? false : t3;
  let t4;
  if ($[0] !== goalPercent) {
    t4 = goalPercent !== undefined && Number.isFinite(goalPercent) && goalPercent > 0 ? `/${String(Math.round(goalPercent))}%` : null;
    $[0] = goalPercent;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const goalLabel = t4;
  let t5;
  if ($[2] !== colors.text) {
    t5 = cn("min-w-0 truncate text-body font-medium tracking-tight", colors.text);
    $[2] = colors.text;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== label || $[5] !== labelRef || $[6] !== t5) {
    t6 = <span ref={labelRef} className={t5}>{label}</span>;
    $[4] = label;
    $[5] = labelRef;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== drained || $[9] !== drainedLabel) {
    t7 = drained && drainedLabel !== undefined && <DrainedChip label={drainedLabel} className="shrink-0" />;
    $[8] = drained;
    $[9] = drainedLabel;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6 || $[12] !== t7 || $[13] !== ticker) {
    t8 = <span className="flex w-full min-w-0 items-center gap-1.5">{t6}{ticker}{t7}</span>;
    $[11] = t6;
    $[12] = t7;
    $[13] = ticker;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== colors.text || $[16] !== decimals || $[17] !== goalContent || $[18] !== goalLabel || $[19] !== percent || $[20] !== percentContent || $[21] !== showPercent) {
    t9 = showPercent && <span className={cn("flex shrink-0 items-baseline gap-1", colors.text)}>{percentContent ?? <AnimatedOutcomePercent value={percent} decimals={decimals} animateOnMount={true} className={cn("shrink-0 text-body-lg font-medium tabular-nums tracking-tight", colors.text)} />}{goalContent ?? (goalLabel !== null && <span className={cn("text-caption font-medium tabular-nums", colors.text)}>{goalLabel}</span>)}</span>;
    $[15] = colors.text;
    $[16] = decimals;
    $[17] = goalContent;
    $[18] = goalLabel;
    $[19] = percent;
    $[20] = percentContent;
    $[21] = showPercent;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t8 || $[24] !== t9) {
    t10 = <span className="flex min-h-0 min-w-0 flex-1 flex-col items-start justify-center gap-0 overflow-hidden leading-5">{t8}{t9}</span>;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}